import type { AIPriority, LayoverAirportPreference, RankingWeights } from '@/src/types/aiRecommendation'
import type { FlightCard, Sectors } from '@/src/types/flight'
import { clamp01 } from './normalization'

const HIGH_PRIORITY_FLOOR = 0.4
const DEPARTURE_PREFERENCE_FLOOR = 0.25
const ARRIVAL_PREFERENCE_FLOOR = 0.20

export interface WeightEnforcementContext {
    hasDepartureTimePreference: boolean
    hasArrivalTimePreference: boolean
    priority?: AIPriority | null
}

function normalizeWeightValues(w: RankingWeights): RankingWeights {
    const sum = w.priceWeight + w.durationWeight + w.layoverWeight + w.departureWeight + w.arrivalWeight
    const denom = sum > 0 ? sum : 1
    return {
        priceWeight: w.priceWeight / denom,
        durationWeight: w.durationWeight / denom,
        layoverWeight: w.layoverWeight / denom,
        departureWeight: w.departureWeight / denom,
        arrivalWeight: w.arrivalWeight / denom
    }
}

export function sanitizeAndNormalizeWeights(weights: RankingWeights, ctx?: WeightEnforcementContext | null): RankingWeights {
    const safe = {
        priceWeight: clamp01(weights?.priceWeight ?? 0.25),
        durationWeight: clamp01(weights?.durationWeight ?? 0.25),
        layoverWeight: clamp01(weights?.layoverWeight ?? 0.25),
        departureWeight: clamp01(weights?.departureWeight ?? 0.25),
        arrivalWeight: clamp01(weights?.arrivalWeight ?? 0.05)
    }

    if (ctx?.hasDepartureTimePreference) {
        safe.departureWeight = Math.max(safe.departureWeight, DEPARTURE_PREFERENCE_FLOOR)
    }
    if (ctx?.hasArrivalTimePreference) {
        safe.arrivalWeight = Math.max(safe.arrivalWeight, ARRIVAL_PREFERENCE_FLOOR)
    }

    if (ctx?.priority?.departureTime === 'HIGH') {
        safe.departureWeight = Math.max(safe.departureWeight, HIGH_PRIORITY_FLOOR)
    }
    if (ctx?.priority?.arrivalTime === 'HIGH') {
        safe.arrivalWeight = Math.max(safe.arrivalWeight, HIGH_PRIORITY_FLOOR)
    }

    return normalizeWeightValues(safe)
}

export function computeLayoverScore(stops: number): number {
    const s = Number.isFinite(stops) ? Math.max(0, stops) : 0
    return Math.max(0, 1 - s * 0.15)
}

/**
 * Extracts via (connecting) airport codes from sector segments.
 * These are the arrival airports of all intermediate segments (excluding the final destination).
 */
export function getViaAirportCodes(flight: FlightCard, sectors?: Sectors): string[] {
    if (!flight.sectorKeys?.length || !sectors) return []
    const sector = sectors[flight.sectorKeys[0]]
    const segments = sector?.flights?.segments
    if (!segments || segments.length <= 1) return []
    return segments.slice(0, -1).map(s => s.arrival?.airportCode).filter(Boolean)
}

export interface LayoverAirportMatchResult {
    isMatch: boolean
}

/**
 * Checks whether a flight's via airports match the user's preferred layover airports.
 * Used purely for boost multiplier — not part of the weighted base score.
 */
export function checkLayoverAirportMatch(
    viaAirportCodes: string[],
    preference: LayoverAirportPreference | null | undefined
): LayoverAirportMatchResult {
    if (!preference?.codes?.length) {
        return { isMatch: false }
    }

    if (viaAirportCodes.length === 0) {
        return { isMatch: false }
    }

    const preferredSet = new Set(preference.codes)
    const hasMatch = viaAirportCodes.some(code => preferredSet.has(code))

    return { isMatch: hasMatch }
}

